import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { genererCanauxRuban, MATCAP_GAIN, CHROME_BOX } from './lib/matcap.js';

// Contrat du rig partagé : interdit. Le plateau a son propre spot.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'zoetrope',
  family: 'objects',
  danceability: 0.66,
  requiresTier: 0,
  energyLevel: 0.58,
  bpmMin: 85,
  bpmMax: 130,
  movement: 0.60,
  tonality: 0.75,
  enabled: true
};

// ZOETROPE : un zootrope 3D. Un plateau de bois verni porte trois anneaux de
// petites sculptures laquées, chacune un peu différente de sa voisine ; vues
// au bon rythme, elles s'animent sur place (une fleur qui éclôt, une goutte
// qui rebondit, un cœur qui bat...). Électro-pop, nu-disco, house, pop indé.
//
// L'ILLUSION SANS STROBOSCOPE (R10). Un anneau ne tourne pas en continu : il
// avance par CRANS exacts d'un emplacement (360° / N), et reste fixe entre
// deux crans. La sculpture j a la phase fract(-j c / N) de son animation
// (c = nombre de boucles sur le tour) ; après S crans, l'emplacement s
// montre la sculpture s - S, de phase fract(S / F - s c / N) avec F = N / c
// images par boucle : chaque emplacement montre l'image SUIVANTE à chaque
// cran. Aucun flash, aucune image noire : un cran remplace une sculpture
// par sa voisine au même endroit, la luminosité globale ne bouge pas.
// Par-dessus, le plateau entier tourne lentement (un tour en 32 temps),
// intégré au tempo. ?zoesmooth=1 : rotation continue à la même vitesse
// moyenne, sans crans - les sculptures défilent, figées, plus d'illusion.
//
// LES CADENCES (une boucle = 1, 2 et 4 temps : les trois retombent ensemble
// sur la mesure, puisque S = floor(horloge x crans/temps) est un multiple de
// F à chaque mesure) :
//   extérieur  N 24, une boucle par temps : 12 images (c = 2), 12 crans par
//              temps jusqu'à 110 BPM (22/s) ; au-delà 8 images (c = 3),
//              8 crans par temps (14,7 à 17,3/s)
//   milieu     N 18, une boucle en 2 temps : 18 images, 9 crans par temps
//              (12,8 à 19,5/s)
//   intérieur  N 12, une boucle en 4 temps : 12 images, 3 crans par temps.
//              Douze sculptures ne font pas plus de douze images : sur
//              quatre temps, c'est 3 par temps, pas 8 (le brief demandait
//              12 à 24 crans par seconde pour tous ; impossible ici).
// LE DROP double la cadence pendant 4 mesures sans doubler les crans par
// seconde (l'extérieur passerait à 48/s, au-delà de l'affichage) : chaque
// cran saute DEUX emplacements. Animations deux fois plus rapides, plateau
// qui accélère ; au bout des 16 temps le décalage est un nombre entier de
// boucles, les anneaux retombent en phase.
//
// LES SCULPTURES : un seul InstancedMesh par anneau. La géométrie de base
// est 7 grilles (u, v) - des « pièces » - que le vertex shader plie selon
// l'animation de l'anneau et la phase de l'instance : sphères, capsules,
// tores, cornets, feuilles. Une pièce inutile est écrasée en un point (des
// triangles d'aire nulle). Normales par différences finies de la surface
// (produit vectoriel), orientées vers l'œil par gl_FrontFacing : l'ordre
// des indices et le produit vectoriel ont la même orientation, une pièce
// fermée montre donc toujours sa normale extérieure. Matcap laquée (R5),
// une couleur de palette par anneau, MSAA dans une cible privée.
//
// LE PLATEAU : bois sombre verni à cernes CONCENTRIQUES (un cran ne fait pas
// sauter le veinage), clous de laiton sur le pourtour qui tournent avec la
// rotation lente seule ; spot chaud au-dessus (tache douce, reflet du vernis
// calculé avec le vrai vecteur de vue) ; ombre de contact sous chaque
// emplacement, calculée dans le shader du plateau à partir de l'angle de
// l'anneau (elle suit les crans).

const TAU = Math.PI * 2;
const ANIMS = ['bloom', 'bounce', 'twist', 'heart', 'wave', 'orbit', 'dancer'];
const PARTS = 7;
// the rings: count, loop length in beats, radius on the plate, scale
const RINGS = [
  { N: 24, loopBeats: 1, radius: 0.86, scale: 0.19 },
  { N: 18, loopBeats: 2, radius: 0.665, scale: 0.21 },
  { N: 12, loopBeats: 4, radius: 0.47, scale: 0.23 }
];
const TIER = {
  0: { rings: 2, C: 12, R: 8, msaa: false },
  1: { rings: 3, C: 16, R: 10, msaa: true },
  2: { rings: 3, C: 20, R: 12, msaa: true }
};
const PLATE_R = 1.0, PLATE_H = 0.07;
const ELEVATION_DEG = 35, FOV_DEG = 30;
const PLATE_WIDTH = 0.70;                 // of the screen's width
const SLOW_BEATS = 32;                    // one slow turn
const OUTER_FAST_BPM = 110;               // above: 8 crans per beat, not 12
const DROP_BEATS = 16;
const NO_BPM = 110, SNAP_BEATS = 0.35, PHRASE_BEATS = 16;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const SAFE_FLOOR = 0.45;
const EXCLUSIONS = [['tr', 0.22, 0.12], ['tl', 0.08, 0.12], ['br', 0.22, 0.10]];

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hashStr(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h >>> 0;
}

// --- the frame counts -------------------------------------------------------
// cycles round the ring, frames per loop, crans per beat
export function ringCadence(ringIdx, bpm) {
  const r = RINGS[ringIdx];
  const c = ringIdx === 0 ? (bpm > OUTER_FAST_BPM ? 3 : 2) : 1;
  const F = r.N / c;
  return { c, F, perBeat: F / r.loopBeats };
}
// the phase shown at slot s after S crans (S may be fractional in smooth mode:
// then the slots hold moving sculptures, this is the phase of the one there)
export function phaseAtSlot(s, S, N, c) {
  const j = ((Math.round(s - S) % N) + N) % N;
  return (((-j * c / N) % 1) + 1) % 1;
}

// --- geometry: PARTS grids of (C+1) x (R+1), position = (u, v, part) --------
const GEOMS = new Map();
function partsGeometry(C, R) {
  const key = C + 'x' + R;
  if (GEOMS.has(key)) return GEOMS.get(key);
  const nv = (C + 1) * (R + 1);
  const pos = new Float32Array(PARTS * nv * 3), idx = [];
  for (let p = 0; p < PARTS; p++) {
    for (let j = 0; j <= R; j++) for (let i = 0; i <= C; i++) {
      const o = (p * nv + j * (C + 1) + i) * 3;
      pos[o] = i / C; pos[o + 1] = j / R; pos[o + 2] = p;
    }
    for (let j = 0; j < R; j++) for (let i = 0; i < C; i++) {
      const a = p * nv + j * (C + 1) + i, b = a + 1, c = a + C + 1, d = c + 1;
      // (a, b, c): cross(P(u+du) - P, P(v+dv) - P) is the geometric normal,
      // the same orientation as the shader's finite-difference normal
      idx.push(a, b, c, b, d, c);
    }
  }
  const base = new THREE.BufferGeometry();
  base.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  base.setIndex(idx);
  GEOMS.set(key, base);
  return base;
}

// --- GLSL ---------------------------------------------------------------------
const SAFE_GLSL = `
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt(vec2 ndc) {
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

// The sculptures, in their own unit frame: y up, base on y = 0, about one
// unit tall; x tangential, z radial (outward, toward the viewer on the
// front slots).
const SHAPES_GLSL = `
  const float PI = 3.14159265;
  const float TAU = 6.2831853;
  uniform float uRows;
  vec3 sph(vec2 uv) { float th = TAU * uv.x, ph = PI * uv.y; return vec3(sin(ph) * cos(th), cos(ph), sin(ph) * sin(th)); }
  vec3 capsule(vec2 uv, vec3 a, vec3 b, float r) {
    vec3 s = sph(uv);
    vec3 d = a - b; float L = length(d);
    d = L > 1e-5 ? d / L : vec3(0.0, 1.0, 0.0);
    vec3 ref = abs(d.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 t1 = normalize(cross(d, ref)), t2 = cross(t1, d);
    vec3 c = s.y >= 0.0 ? a : b;
    return c + r * (s.x * t1 + s.y * d + s.z * t2);
  }
  vec3 ball(vec2 uv, vec3 c, float r) { return c + r * sph(uv); }
  vec3 torus(vec2 uv, vec3 c, float R, float r, float tilt) {
    float th = TAU * uv.x, ph = TAU * uv.y;
    vec3 p = vec3((R + r * cos(ph)) * cos(th), r * sin(ph), (R + r * cos(ph)) * sin(th));
    float ct = cos(tilt), st = sin(tilt);
    return c + vec3(p.x, ct * p.y - st * p.z, st * p.y + ct * p.z);
  }
  vec3 pedestal(vec2 uv) { vec3 s = sph(uv); return vec3(s.x * 0.27, 0.035 + s.y * 0.035, s.z * 0.27); }
  vec3 hidden() { return vec3(0.0, 0.03, 0.0); }
  float cdist(float a, float b) { float d = abs(a - b); return min(d, 1.0 - d); }

  // 0 BLOOM: petals on a cone that opens and closes, the tips curling back
  vec3 bloom(float part, vec2 uv, float ph) {
    float o = 0.5 - 0.5 * cos(TAU * ph);
    vec3 base = vec3(0.0, 0.44, 0.0);
    if (part < 0.5) {
      float th = TAU * uv.x;
      float lobe = pow(abs(cos(2.5 * th)), 0.8);
      float s = uv.y * 0.42 * mix(0.45, 1.0, lobe);
      float beta = mix(0.10, 0.44, o) * PI + 0.9 * o * uv.y * uv.y;
      float rho = 0.06 + s * sin(beta), h = s * cos(beta);
      return base + vec3(rho * cos(th), h, rho * sin(th));
    }
    if (part < 1.5) return ball(uv, base + vec3(0.0, 0.05 + 0.04 * o, 0.0), 0.07);
    if (part < 2.5) return capsule(uv, base, vec3(0.0, 0.06, 0.0), 0.03);
    if (part < 3.5) {
      // a leaf on the stem
      float th = TAU * uv.x; vec3 sp = sph(uv);
      vec3 c = vec3(0.09, 0.22, 0.0);
      return c + vec3(sp.y * 0.10, sp.y * 0.05 + sp.x * 0.012, sp.z * 0.04);
    }
    if (part > 5.5) return pedestal(uv);
    return hidden();
  }
  // 1 BOUNCE: a drop falls, squashes on the pedestal, bounces; a splash ring
  vec3 bounce(float part, vec2 uv, float ph) {
    float u = 2.0 * ph - 1.0;
    float h = 0.55 * (1.0 - u * u);
    float sq = exp(-pow(min(ph, 1.0 - ph) / 0.07, 2.0));
    float st = 0.30 * abs(u) * (1.0 - sq);
    if (part < 0.5) {
      vec3 s = sph(uv);
      float r = 0.17;
      float sy = 1.0 - 0.45 * sq + st, sxz = 1.0 + 0.38 * sq - 0.15 * st;
      vec3 p = vec3(s.x * sxz, s.y * sy, s.z * sxz) * r;
      // pointed at the top while it moves
      p.y += r * 0.5 * st * max(s.y, 0.0) * max(s.y, 0.0);
      return p + vec3(0.0, 0.07 + r * sy + h, 0.0);
    }
    if (part < 1.5) {
      float t = ph < 0.5 ? ph : ph - 1.0;              // around the impact
      float a = clamp(t / 0.22, 0.0, 1.0);
      float thick = t > 0.0 ? 0.02 * (1.0 - a) : 0.0;
      return torus(uv, vec3(0.0, 0.075, 0.0), 0.12 + 0.20 * a, thick, 0.0);
    }
    if (part > 5.5) return pedestal(uv);
    return hidden();
  }
  // 2 TWIST: a rounded square column twisting and untwisting
  vec3 twist(float part, vec2 uv, float ph) {
    if (part < 0.5) {
      float k = uv.y, rows = uRows;
      float f = clamp(min(k, 1.0 - k) * rows, 0.0, 1.0);
      float t = clamp((k - 1.0 / rows) / (1.0 - 2.0 / rows), 0.0, 1.0);
      float H = 0.78, y = 0.07 + H * (1.0 - t);
      float th = TAU * uv.x;
      float c = cos(th), s = sin(th);
      float sq = 1.0 / pow(pow(abs(c), 4.0) + pow(abs(s), 4.0), 0.25);
      float w = 0.12 * (1.0 + 0.18 * sin(PI * (1.0 - t)) * (0.5 - 0.5 * cos(TAU * ph))) * f;
      float tw = 1.5 * PI * sin(TAU * ph) * (1.0 - t) + 0.25 * PI;
      float a = th + tw;
      return vec3(w * sq * cos(a), y, w * sq * sin(a));
    }
    if (part > 5.5) return pedestal(uv);
    return hidden();
  }
  // 3 HEART: lub-dub
  vec3 heart(float part, vec2 uv, float ph) {
    float beat = exp(-pow(cdist(ph, 0.08) / 0.06, 2.0)) + 0.6 * exp(-pow(cdist(ph, 0.30) / 0.06, 2.0));
    vec3 c = vec3(0.0, 0.58, 0.0);
    if (part < 0.5) {
      vec3 s = sph(uv);
      vec3 p = vec3(s.x, s.y * 0.95 + 0.55 * sqrt(abs(s.x)) - 0.28, s.z * 0.55);
      return c + p * 0.27 * (1.0 + 0.16 * beat);
    }
    if (part < 1.5) return capsule(uv, c - vec3(0.0, 0.22, 0.0), vec3(0.0, 0.06, 0.0), 0.025);
    if (part > 5.5) return pedestal(uv);
    return hidden();
  }
  // 4 WAVE: a sheet whose profile is an arc of growing curvature: a swell,
  // a crest that curls over, the curl breaking along the wave
  vec3 wave(float part, vec2 uv, float ph) {
    if (part < 0.5) {
      float x = (uv.x - 0.5) * 0.56;
      float w = 0.5 - 0.5 * cos(TAU * (ph + 0.12 * (uv.x - 0.5)));
      float k = mix(1.4, 5.6, w);
      float s = uv.y * 0.95 * mix(1.0, 0.9, w);
      float y = (1.0 - cos(k * s)) / k, z = sin(k * s) / k;
      return vec3(x, 0.07 + 0.80 * y, -0.24 + 0.72 * z);
    }
    if (part > 5.5) return pedestal(uv);
    return hidden();
  }
  // 5 ORBIT: a ringed planet and its moon
  vec3 orbit(float part, vec2 uv, float ph) {
    vec3 c = vec3(0.0, 0.56, 0.0);
    if (part < 0.5) return ball(uv, c, 0.19);
    if (part < 1.5) {
      float a = TAU * ph;
      vec3 m = vec3(0.40 * cos(a), 0.0, 0.40 * sin(a));
      float t = 0.30;
      m = vec3(m.x, cos(t) * m.y - sin(t) * m.z, sin(t) * m.y + cos(t) * m.z);
      return ball(uv, c + m, 0.065);
    }
    if (part < 2.5) return torus(uv, c, 0.28, 0.014, 0.35);
    if (part < 3.5) return capsule(uv, c - vec3(0.0, 0.15, 0.0), vec3(0.0, 0.06, 0.0), 0.025);
    if (part > 5.5) return pedestal(uv);
    return hidden();
  }
  // 6 DANCER: capsules doing a step, hips swaying, arms up in turn
  vec3 dancer(float part, vec2 uv, float ph) {
    float t = TAU * ph, sn = sin(t), cs = cos(t);
    vec3 pelvis = vec3(0.05 * sn, 0.44 - 0.03 * (0.5 - 0.5 * cos(2.0 * t)), 0.0);
    vec3 chest = vec3(-0.025 * sn, 0.70 - 0.02 * (0.5 - 0.5 * cos(2.0 * t)), 0.02 * sn);
    if (part < 0.5) return ball(uv, chest + vec3(-0.01 * sn, 0.15, 0.0), 0.075);
    if (part < 1.5) return capsule(uv, chest, pelvis, 0.085);
    vec3 shL = chest + vec3(-0.11, -0.01, 0.0), shR = chest + vec3(0.11, -0.01, 0.0);
    if (part < 2.5) return capsule(uv, vec3(-0.22, 0.80 + 0.17 * sn, 0.08 * cs), shL, 0.03);
    if (part < 3.5) return capsule(uv, vec3(0.22, 0.80 - 0.17 * sn, -0.08 * cs), shR, 0.03);
    vec3 hipL = pelvis + vec3(-0.055, 0.0, 0.0), hipR = pelvis + vec3(0.055, 0.0, 0.0);
    if (part < 4.5) return capsule(uv, hipL, vec3(-0.09 + 0.03 * sn, 0.11 + 0.12 * max(sn, 0.0), 0.08 * sn), 0.04);
    if (part < 5.5) return capsule(uv, hipR, vec3(0.09 + 0.03 * sn, 0.11 + 0.12 * max(-sn, 0.0), -0.08 * sn), 0.04);
    return pedestal(uv);
  }
  vec3 shape(float anim, float part, vec2 uv, float ph) {
    if (anim < 0.5) return bloom(part, uv, ph);
    if (anim < 1.5) return bounce(part, uv, ph);
    if (anim < 2.5) return twist(part, uv, ph);
    if (anim < 3.5) return heart(part, uv, ph);
    if (anim < 4.5) return wave(part, uv, ph);
    if (anim < 5.5) return orbit(part, uv, ph);
    return dancer(part, uv, ph);
  }
`;

const sculptVertexShader = `
  precision highp float;
  ${SHAPES_GLSL}
  attribute float iSlot;
  uniform float uN;
  uniform float uC;
  uniform float uRadius;
  uniform float uScale;
  uniform float uRingAngle;
  uniform float uAnim;
  varying vec3 vN;
  varying vec3 vWorld;
  varying vec4 vClip;
  varying float vUp;
  void main() {
    float part = position.z;
    vec2 uv = position.xy;
    float ph = fract(-iSlot * uC / uN);
    vec3 p = shape(uAnim, part, uv, ph);
    // the normal: finite differences of the surface, near the poles taken a
    // row in (the pole itself has no u derivative)
    vec2 q = vec2(uv.x, clamp(uv.y, 0.02, 0.98));
    float e = 0.004;
    vec3 p0 = shape(uAnim, part, q, ph);
    vec3 du = shape(uAnim, part, q + vec2(e, 0.0), ph) - p0;
    vec3 dv = q.y < 0.5 ? shape(uAnim, part, q + vec2(0.0, e), ph) - p0 : p0 - shape(uAnim, part, q - vec2(0.0, e), ph);
    vec3 n = cross(du, dv);
    n = dot(n, n) > 1e-20 ? normalize(n) : vec3(0.0, 1.0, 0.0);
    // the slot: radial r, tangential t = up x r (x, y, z -> t, up, r keeps handedness)
    float a = TAU * iSlot / uN + uRingAngle;
    vec3 r = vec3(cos(a), 0.0, sin(a)), t = vec3(sin(a), 0.0, -cos(a)), up = vec3(0.0, 1.0, 0.0);
    vec3 w = uRadius * r + uScale * (p.x * t + p.y * up + p.z * r);
    vec3 nw = n.x * t + n.y * up + n.z * r;
    vN = normalize((viewMatrix * vec4(nw, 0.0)).xyz);
    vUp = nw.y;
    vWorld = w;
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
    vClip = gl_Position;
  }
`;
const sculptFragmentShader = `
  precision mediump float;
  ${SAFE_GLSL}
  uniform sampler2D uMat;
  uniform vec3 uTint;
  uniform float uShowNormals;
  varying vec3 vN;
  varying vec3 vWorld;
  varying vec4 vClip;
  varying float vUp;
  void main() {
    vec3 n = normalize(vN);
    if (!gl_FrontFacing) n = -n;
    if (uShowNormals > 0.5) { gl_FragColor = vec4(n * 0.5 + 0.5, 1.0); return; }
    if (n.z < 0.0) n = normalize(vec3(n.xy, 0.02));
    vec4 m = texture2D(uMat, n.xy * 0.49 + 0.5);
    float body = m.r * m.r * ${MATCAP_GAIN.toFixed(2)};
    float box = m.a * ${CHROME_BOX.toFixed(2)};
    // the spot: a soft pool on the plate, tops lit a little more
    float rr = length(vWorld.xz);
    float spot = mix(0.30, 1.0, smoothstep(1.35, 0.55, rr)) * (0.85 + 0.15 * clamp(vUp, 0.0, 1.0));
    float rim = pow(1.0 - n.z, 4.0) * 0.30;
    vec3 col = uTint * (body * 1.9 + rim) * spot + vec3(1.0, 0.94, 0.84) * box * 0.62 * spot;
    gl_FragColor = vec4(col * safeAtt(vClip.xy / vClip.w), 1.0);
  }
`;

// the plate: dark varnished wood, concentric grain, brass studs on the rim,
// a warm spot, contact shadows under the slots
const plateVertexShader = `
  precision highp float;
  varying vec3 vWorld;
  varying vec3 vNw;
  varying vec4 vClip;
  void main() {
    vec4 w = modelMatrix * vec4(position, 1.0);
    vWorld = w.xyz;
    vNw = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * viewMatrix * w;
    vClip = gl_Position;
  }
`;
const plateFragmentShader = `
  precision highp float;
  ${SAFE_GLSL}
  uniform vec4 uRing[3];        // radius, N, angle, scale
  uniform float uRingCount;
  uniform float uSlow;          // the slow rotation (the studs)
  uniform vec3 uLightPos;
  varying vec3 vWorld;
  varying vec3 vNw;
  varying vec4 vClip;
  float hash(float x) { return fract(sin(x * 91.7) * 4375.85); }
  float vnoise(float x) { float i = floor(x), f = fract(x); f = f * f * (3.0 - 2.0 * f); return mix(hash(i), hash(i + 1.0), f); }
  void main() {
    vec2 p = vWorld.xz;
    float r = length(p);
    float top = step(0.5, vNw.y);
    // concentric grain: a function of the radius only
    float g = vnoise(r * 38.0) * 0.6 + vnoise(r * 140.0) * 0.4;
    float rings = 0.5 + 0.5 * sin(r * 160.0 + g * 5.0);
    vec3 wood = mix(vec3(0.050, 0.022, 0.010), vec3(0.105, 0.052, 0.024), rings * 0.7 + g * 0.3);
    // the rim: a slightly darker band with brass studs (they turn with the slow rotation)
    float ang = atan(p.y, p.x) - uSlow;
    float k = floor(ang / (6.2831853 / 32.0) + 0.5);
    float sa = k * 6.2831853 / 32.0 + uSlow;
    vec2 stud = 0.955 * vec2(cos(sa), sin(sa));
    float ds = length(p - stud);
    float studM = (1.0 - smoothstep(0.011, 0.016, ds)) * top;
    float band = smoothstep(0.915, 0.925, r) * top;
    wood *= 1.0 - 0.35 * band;
    // the spot: a soft pool
    float spot = mix(0.22, 1.0, smoothstep(1.30, 0.45, r));
    vec3 col = wood * spot * 1.6;
    // the varnish: the spot's reflection, with the real view vector
    vec3 L = normalize(uLightPos - vWorld), V = normalize(cameraPosition - vWorld);
    vec3 H = normalize(L + V);
    vec3 nrm = normalize(vNw);
    float spec = pow(max(dot(nrm, H), 0.0), 90.0) * 0.30 + pow(max(dot(nrm, H), 0.0), 12.0) * 0.025;
    col += vec3(1.0, 0.82, 0.60) * spec * (0.4 + 0.6 * top);
    // brass studs
    float sl = 0.5 + 0.5 * dot(normalize(p - stud + vec2(0.0001)), normalize(vec2(-0.3, -0.9)));
    col = mix(col, vec3(0.55, 0.38, 0.14) * (0.35 + 0.65 * sl) * spot, studM);
    // contact shadows: the nearest slot of each ring
    float sh = 1.0;
    for (int i = 0; i < 3; i++) {
      if (float(i) >= uRingCount) break;
      vec4 R = uRing[i];
      float step0 = 6.2831853 / R.y;
      float rel = atan(p.y, p.x) - R.z;
      float kk = floor(rel / step0 + 0.5);
      float a = kk * step0 + R.z;
      // a little outward of the slot (the spot is above the centre), wider than the pedestal
      vec2 c = (R.x + 0.10 * R.w) * vec2(cos(a), sin(a));
      float d = length(p - c) / R.w;
      sh *= 1.0 - 0.70 * exp(-d * d / 0.16) * top;
    }
    col *= sh;
    gl_FragColor = vec4(col * safeAtt(vClip.xy / vClip.w), 1.0);
  }
`;

// the axis: brass, the chrome channels tinted
const axisVertexShader = `
  precision highp float;
  varying vec3 vN;
  varying vec4 vClip;
  varying vec3 vWorld;
  void main() {
    vec4 w = modelMatrix * vec4(position, 1.0);
    vWorld = w.xyz;
    vN = normalize((viewMatrix * vec4(mat3(modelMatrix) * normal, 0.0)).xyz);
    gl_Position = projectionMatrix * viewMatrix * w;
    vClip = gl_Position;
  }
`;
const axisFragmentShader = `
  precision mediump float;
  ${SAFE_GLSL}
  uniform sampler2D uMat;
  varying vec3 vN;
  varying vec4 vClip;
  varying vec3 vWorld;
  void main() {
    vec3 n = normalize(vN);
    if (n.z < 0.0) n = normalize(vec3(n.xy, 0.02));
    vec4 m = texture2D(uMat, n.xy * 0.49 + 0.5);
    float body = m.b * m.b * ${MATCAP_GAIN.toFixed(2)};
    vec3 brass = vec3(0.95, 0.62, 0.22);
    vec3 col = brass * body * 2.2 + vec3(1.0, 0.85, 0.6) * m.a * 0.7;
    gl_FragColor = vec4(col * safeAtt(vClip.xy / vClip.w), 1.0);
  }
`;
const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const displayFragmentShader = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb, 1.0); }
`;

// --- colours: one per ring, saturated, hues at least 0.25 apart (R4) ----------
function hueDist(a, b) { const d = Math.abs(a - b) % 1; return Math.min(d, 1 - d); }
export function ringColours(palette, count) {
  const hsl = { h: 0, s: 0, l: 0 }, cand = [];
  if (palette) for (const k of ['vividColors', 'dominantColors']) for (const c of (palette[k] || [])) { new THREE.Color(c).getHSL(hsl); if (hsl.s > 0.15) cand.push(hsl.h); }
  const hues = [];
  for (const h of cand) if (hues.length < count && hues.every((x) => hueDist(x, h) >= 0.25)) hues.push(h);
  if (!hues.length) hues.push(0.95);
  while (hues.length < count) {
    let bh = 0, bd = -1;
    for (let k = 0; k < 72; k++) { const h = k / 72, d = Math.min(...hues.map((x) => hueDist(x, h))); if (d > bd) { bd = d; bh = h; } }
    hues.push(bh);
  }
  // saturated, luminance 0.30 (the lacquer's body stays under the bloom)
  return hues.map((h) => {
    const c = new THREE.Color().setHSL(h, 0.92, 0.5);
    const l = 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
    return c.multiplyScalar(0.30 / Math.max(l, 1e-3));
  });
}

// --- the framing --------------------------------------------------------------
// The plate's projected width is PLATE_WIDTH of the screen, its centre under
// the cover; narrower until the whole plate (sculptures included) sits inside
// the 5 % margins and out of the exclusion zones. Returns the camera pose.
const _fc = new THREE.PerspectiveCamera(), _v = new THREE.Vector3();
const HULL = [];
for (let k = 0; k < 48; k++) {
  const a = k / 48 * TAU;
  HULL.push([Math.cos(a) * PLATE_R, 0, Math.sin(a) * PLATE_R], [Math.cos(a) * PLATE_R, -PLATE_H, Math.sin(a) * PLATE_R]);
  HULL.push([Math.cos(a) * 0.95, 0.24, Math.sin(a) * 0.95]);       // the outer sculptures' tops
}
HULL.push([0, 0.62, 0]);                                            // the axis' knob
function poseCamera(cam, dist, shift) {
  const el = ELEVATION_DEG * Math.PI / 180;
  const dir = new THREE.Vector3(0, -Math.sin(el), Math.cos(el));
  const up = new THREE.Vector3(0, Math.cos(el), Math.sin(el));
  const target = up.clone().multiplyScalar(shift);
  cam.position.copy(target).addScaledVector(dir, -dist);
  cam.up.set(0, 1, 0);
  cam.lookAt(target);
  cam.updateMatrixWorld();
}
export function fitPlate(aspect, centerY) {
  const cam = _fc;
  cam.fov = FOV_DEG; cam.aspect = aspect; cam.near = 0.1; cam.far = 100; cam.updateProjectionMatrix();
  const tanH = Math.tan(FOV_DEG * Math.PI / 360);
  const solve = (w, yc) => {
    let dist = 4, shift = 0;
    for (let it = 0; it < 16; it++) {
      poseCamera(cam, dist, shift);
      let xl = 9, xr = -9;
      for (const h of HULL) { if (h[1] !== 0) continue; _v.set(h[0], h[1], h[2]).project(cam); xl = Math.min(xl, _v.x); xr = Math.max(xr, _v.x); }
      dist *= (xr - xl) / (2 * w);
      _v.set(0, 0, 0).project(cam);
      shift += (_v.y - yc) * dist * tanH;
    }
    poseCamera(cam, dist, shift);
    return { dist, shift };
  };
  const inside = () => {
    for (const h of HULL) {
      _v.set(h[0], h[1], h[2]).project(cam);
      const x = _v.x * aspect, y = _v.y;
      if (Math.abs(y) > 0.9 || Math.abs(x) > aspect - 0.1) return false;
      for (const e of EXCLUSIONS) {
        const ew = e[1] * 2 * aspect, eh = e[2] * 2;
        const x0 = e[0][1] === 'r' ? aspect - ew : -aspect, x1 = e[0][1] === 'r' ? aspect : -aspect + ew;
        const y0 = e[0][0] === 't' ? 1 - eh : -1, y1 = e[0][0] === 't' ? 1 : -1 + eh;
        if (x > x0 && x < x1 && y > y0 && y < y1) return false;
      }
    }
    return true;
  };
  // the cover's centre first; if the plate cannot stay there at any useful
  // size, it may slide down (never up: its back must stay above the cover)
  for (const dy of [0, -0.1, -0.2, -0.3]) {
    for (let w = PLATE_WIDTH; w >= 0.30; w -= 0.01) {
      const s = solve(w, centerY + dy);
      if (inside()) return { dist: s.dist, shift: s.shift, width: w, dy };
    }
  }
  const s = solve(0.30, centerY);
  return { dist: s.dist, shift: s.shift, width: 0.30, dy: 0 };
}

// --- module caches: on the Pi a material recompiles on every appearance otherwise
let MATCAP_TEX = null;
const MATS = [];
let PLATE_MAT = null, AXIS_MAT = null;
const SAFE_U = () => ({ uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather });
function matcapTex() {
  if (MATCAP_TEX) return MATCAP_TEX;
  const m = genererCanauxRuban(256);
  MATCAP_TEX = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat);
  MATCAP_TEX.minFilter = MATCAP_TEX.magFilter = THREE.LinearFilter; MATCAP_TEX.needsUpdate = true;
  return MATCAP_TEX;
}
function ringMaterial(i) {
  if (MATS[i]) return MATS[i];
  const U = Object.assign({
    uN: { value: 24 }, uC: { value: 1 }, uRadius: { value: 0.8 }, uScale: { value: 0.2 }, uRingAngle: { value: 0 },
    uAnim: { value: 0 }, uRows: { value: 10 }, uMat: { value: matcapTex() }, uTint: { value: new THREE.Color(1, 0, 0) }, uShowNormals: { value: 0 }
  }, SAFE_U());
  MATS[i] = new THREE.ShaderMaterial({ uniforms: U, vertexShader: sculptVertexShader, fragmentShader: sculptFragmentShader, side: THREE.DoubleSide });
  return MATS[i];
}
function plateMaterial() {
  if (PLATE_MAT) return PLATE_MAT;
  const U = Object.assign({
    uRing: { value: [new THREE.Vector4(), new THREE.Vector4(), new THREE.Vector4()] }, uRingCount: { value: 3 },
    uSlow: { value: 0 }, uLightPos: { value: new THREE.Vector3(0, 3.2, 0) }
  }, SAFE_U());
  PLATE_MAT = new THREE.ShaderMaterial({ uniforms: U, vertexShader: plateVertexShader, fragmentShader: plateFragmentShader });
  return PLATE_MAT;
}
function axisMaterial() {
  if (AXIS_MAT) return AXIS_MAT;
  AXIS_MAT = new THREE.ShaderMaterial({ uniforms: Object.assign({ uMat: { value: matcapTex() } }, SAFE_U()), vertexShader: axisVertexShader, fragmentShader: axisFragmentShader });
  return AXIS_MAT;
}

export class ZoetropeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 1;
    this._L = TIER[this._tier];
    const fa = urlParam('zoeanim');
    this._forceAnim = fa && ANIMS.includes(fa) ? ANIMS.indexOf(fa) : null;
    this._smooth = urlParam('zoesmooth') === '1';
    this._forceDrop = urlParam('zoedrop') === '1';
    this._showNormals = urlParam('zoenormals') === '1';
    this._post = { bloomStrength: 0.22, bloomThreshold: 0.85, grain: 0, vignette: 0.30, aberration: 0.0008, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
    if (urlParam('zoeprobe') === '1' && typeof window !== 'undefined') window.__zoetrope = this;
  }

  get postProcessing() { return this._post; }

  init(scene, camera) {
    this._scene = scene;
    // a PRIVATE camera: the shared one drifts by 0.22 in position, made for
    // a 68-degree view from 6 units away - at 30 degrees and 3 units it
    // moved the plate by a quarter of the frame and cut its front off
    this._cam = new THREE.PerspectiveCamera(FOV_DEG, 1.78, 0.1, 100);
    const L = this._L;
    this._private = new THREE.Scene();

    // the rings
    const base = partsGeometry(L.C, L.R);
    this._rings = [];
    for (let i = 0; i < L.rings; i++) {
      const R = RINGS[i];
      const g = new THREE.InstancedBufferGeometry();
      g.setAttribute('position', base.getAttribute('position'));
      g.setIndex(base.getIndex());
      const slots = new Float32Array(R.N); for (let k = 0; k < R.N; k++) slots[k] = k;
      g.setAttribute('iSlot', new THREE.InstancedBufferAttribute(slots, 1));
      g.instanceCount = R.N;
      const mat = ringMaterial(i);
      const U = mat.uniforms;
      U.uN.value = R.N; U.uRadius.value = R.radius; U.uScale.value = R.scale; U.uRows.value = L.R;
      U.uShowNormals.value = this._showNormals ? 1 : 0; U.uRingAngle.value = 0;
      const mesh = new THREE.Mesh(g, mat);
      mesh.frustumCulled = false;
      this._private.add(mesh);
      this._rings.push({ mesh, geom: g, U, anim: 0, extra: 0, S: 0, cad: ringCadence(i, NO_BPM) });
    }
    // the plate: a flat cylinder (top + rim); it turns with the slow rotation only
    const pg = new THREE.CylinderGeometry(PLATE_R, PLATE_R * 0.985, PLATE_H, 128, 1, false);
    pg.translate(0, -PLATE_H / 2, 0);
    this._plate = new THREE.Mesh(pg, plateMaterial());
    this._plateU = this._plate.material.uniforms;
    this._plateU.uRingCount.value = L.rings;
    this._plate.frustumCulled = false;
    this._private.add(this._plate);
    // the brass axis under the cover
    const ag = new THREE.CylinderGeometry(0.045, 0.06, 0.52, 32, 1, false);
    ag.translate(0, 0.26, 0);
    const knob = new THREE.SphereGeometry(0.075, 32, 16); knob.translate(0, 0.55, 0);
    const collar = new THREE.CylinderGeometry(0.13, 0.15, 0.04, 48); collar.translate(0, 0.02, 0);
    this._axisGeoms = [ag, knob, collar];
    this._axis = new THREE.Group();
    for (const gg of this._axisGeoms) { const m = new THREE.Mesh(gg, axisMaterial()); m.frustumCulled = false; this._axis.add(m); }
    this._private.add(this._axis);

    // MSAA: the zoetrope in a private scene, shown by a full-screen quad
    this._msaaTarget = null;
    this._dispU = { uTex: { value: null } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._dispU, vertexShader: quadVertexShader, fragmentShader: displayFragmentShader, depthTest: false, depthWrite: false
    }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    // state
    this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._bpmCad = NO_BPM;
    this._slow = 0;
    this._drop = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._trackSeen = undefined; this._paletteKey = null;
    this._phraseCount = 0; this._lastPhraseAt = 0;
    this._fitKey = ''; this._pose = null;
    this._log = [];
    this._seedAnims('');
  }

  _note(e) { this._log.push(e); if (this._log.length > 40) this._log.shift(); }

  // the animations: an order seeded by the track, three different at a time
  _seedAnims(key) {
    let h = hashStr(key || 'zoetrope');
    const order = ANIMS.map((_, i) => i);
    for (let i = order.length - 1; i > 0; i--) { h = Math.imul(h ^ (h >>> 15), 2246822507) >>> 0; const j = h % (i + 1); [order[i], order[j]] = [order[j], order[i]]; }
    this._order = order; this._orderIdx = 0;
    for (const r of this._rings) r.anim = this._nextAnim();
    this._applyAnims();
  }
  _nextAnim() {
    const used = new Set(this._rings.map((r) => r.anim));
    for (let k = 0; k < this._order.length; k++) {
      const a = this._order[this._orderIdx % this._order.length]; this._orderIdx++;
      if (!used.has(a)) return a;
    }
    return this._order[0];
  }
  _applyAnims() { for (const r of this._rings) r.U.uAnim.value = this._forceAnim != null ? this._forceAnim : r.anim; }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    if (profile.trackId !== this._trackSeen) {
      this._trackSeen = profile.trackId;
      this._seedAnims(String(profile.title || '') + '\u0001' + String(profile.artist || ''));
      this._phraseCount = 0;
    }
    const pal = profile.artworkPalette;
    const pk = pal ? String((pal.vividColors || []).slice(0, 4)) + '/' + String((pal.dominantColors || []).slice(0, 2)) : '';
    if (pk !== this._paletteKey) {
      this._paletteKey = pk;
      const cols = ringColours(pal, this._rings.length);
      this._rings.forEach((r, i) => r.U.uTint.value.copy(cols[i]));
    }
    this._fitCamera();
    if (profile.playbackState !== 'PLAYING') return;          // frozen, never black

    // the clock, in beats, pulled to the music's bars; a jump snaps (the crans
    // fall on the beats)
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase, ph = profile.phrasePhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? bpm : NO_BPM;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > SNAP_BEATS) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else { const rate = this._bpm / 60; this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5)); }
    } else { this._clock += dt * this._bpm / 60; this._targetOffset = null; }
    if (this._probeClock != null) this._clock = this._probeClock;      // the probe pins the clock (window.__zoetrope)
    const barEdge = Math.floor(this._clockPrev / 4) !== Math.floor(this._clock / 4);

    // the outer ring's cadence follows the tempo, changed on a bar only
    if (barEdge && this._bpm !== this._bpmCad) {
      this._bpmCad = this._bpm;
      this._rings.forEach((r, i) => { r.cad = ringCadence(i, this._bpmCad); r.extra = 0; });
    }

    // the phrase: one ring changes animation, a cut
    let phraseEdge = false;
    if (hasGrid && ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phraseEdge = true; this._phasePhrase = ph; }
    else if (Math.floor(this._clockPrev / PHRASE_BEATS) !== Math.floor(this._clock / PHRASE_BEATS)) phraseEdge = true;
    // a phrase grid that never wraps still changes every two phrases' worth, on a bar
    if (!phraseEdge && barEdge && this._clock - this._lastPhraseAt >= 2 * PHRASE_BEATS) phraseEdge = true;
    if (phraseEdge) {
      this._lastPhraseAt = this._clock;
      const ring = this._rings[this._phraseCount % this._rings.length];
      ring.anim = this._nextAnim();
      this._phraseCount++;
      this._applyAnims();
      this._note({ what: 'phrase', beat: +this._clock.toFixed(2), ring: this._rings.indexOf(ring), anim: ANIMS[ring.anim] });
    }

    // the drop: 4 bars at double cadence (each cran skips two slots)
    let drop = hasGrid && this._tickDrops(profile);
    if (this._forceDrop && barEdge && Math.floor(this._clock / 4) % 8 === 2) drop = true;
    if (drop && !this._drop) {
      // on a bar (the nearest one, or the next): the extra crans then count
      // from a downbeat and the rings stay in phase on every bar of the drop
      const near = 4 * Math.round(this._clock / 4);
      this._drop = { start: Math.abs(this._clock - near) < 1 ? near : 4 * Math.ceil(this._clock / 4) };
      this._note({ what: 'drop', beat: +this._clock.toFixed(2), start: this._drop.start });
    }
    const inDrop = !!this._drop && this._clock - this._drop.start < DROP_BEATS;
    if (this._drop && !inDrop) {
      // done: its 16 beats of extra crans are a whole number of loops
      for (const r of this._rings) r.extra += Math.round(DROP_BEATS * r.cad.perBeat);
      this._drop = null;
    }

    // the slow turn, integrated (twice as fast during the drop)
    this._slow += dt * (this._bpm / 60) * (TAU / SLOW_BEATS) * (inDrop ? 2 : 1);
    this._slow %= TAU;

    this._rings.forEach((r) => {
      const N = r.U.uN.value, q = r.cad.perBeat;
      const n = Math.floor(this._clock * q + 1e-6);
      // during the drop each cran skips a second slot: the crans since its start, counted directly
      // (a jump of the clock inside the drop stays exact)
      const dropExtra = inDrop ? Math.max(0, Math.min(n - Math.round(this._drop.start * q), Math.round(DROP_BEATS * q))) : 0;
      r.S = this._smooth ? this._clock * q : n + r.extra + dropExtra;
      r.U.uC.value = r.cad.c;
      r.U.uRingAngle.value = this._slow + (r.S % N) * TAU / N;
    });
    const PU = this._plateU;
    PU.uSlow.value = this._slow;
    this._rings.forEach((r, i) => PU.uRing.value[i].set(RINGS[i].radius, RINGS[i].N, r.U.uRingAngle.value, RINGS[i].scale));
    this._plate.rotation.y = -this._slow;           // three's +y rotation turns x toward -z: the opposite sense of the rings' angle
  }

  _fitCamera() {
    const cam = this._cam;
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    const cy = safeZoneUniforms.uStageCenter.value.y;   // R19: framed on the stage centre, not the cover
    const key = aspect.toFixed(3) + ',' + cy.toFixed(3);
    if (key !== this._fitKey) {
      this._fitKey = key;
      this._pose = fitPlate(aspect, cy);
      this._note({ what: 'fit', width: +this._pose.width.toFixed(2), dy: this._pose.dy });
    }
    poseCamera(cam, this._pose.dist, this._pose.shift);
  }

  _render(renderer) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const b = renderer.getDrawingBufferSize(_size); w = b.x; h = b.y; }
    const camera = this._cam;
    if (Math.abs(camera.aspect - w / h) > 1e-4) { camera.aspect = w / h; camera.updateProjectionMatrix(); }
    if (!this._msaaTarget || this._msaaTarget.width !== w || this._msaaTarget.height !== h) {
      if (this._msaaTarget) this._msaaTarget.dispose();
      this._msaaTarget = new THREE.WebGLRenderTarget(w, h, { samples: this._L.msaa ? 4 : 0, depthBuffer: true, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    }
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    renderer.setRenderTarget(this._msaaTarget);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._private, camera);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
    this._dispU.uTex.value = this._msaaTarget.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
    if (this._display) { this._display.geometry.dispose(); this._display.material.dispose(); }
    // the shared base geometry and the materials stay cached (module level)
    for (const r of this._rings || []) r.geom.dispose();
    if (this._plate) this._plate.geometry.dispose();
    for (const g of this._axisGeoms || []) g.dispose();
    if (this._msaaTarget) this._msaaTarget.dispose();
    if (typeof window !== 'undefined' && window.__zoetrope === this) window.__zoetrope = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
